import { HttpParams } from '@angular/common/http';
import { Observable, catchError, forkJoin, map, of } from 'rxjs';
import {
  FacilitySource,
  ParentFacilityDto,
  ProspectFacilityDto,
} from '@ups/xplat/api/dto';

/**
 * US 36008: the Kendo valueField the facility typeahead uses while prospects are listed. A prospect
 * has no ParentFacilityID and may share a DisplayName with a Viewpoint facility, so each row gets a
 * key that is unique across both sources (`Customer:<ParentFacilityID>` / `Prospect:<guid>`).
 */
export const FACILITY_SELECTION_KEY = 'SelectionKey';

/** A facility-picker row once prospects are listed: labelled, and keyed uniquely across both sources. */
export type FacilityPickerRow = (ParentFacilityDto | ProspectFacilityDto) & {
  Source: FacilitySource;
  SelectionKey: string;
};

/**
 * True when the search is scoped to a picked customer (`scopeQueryWhenFormControlIsSet` put a
 * non-empty value on its query parameter). A prospect is never a picked customer's facility.
 */
export function isScopedFacilitySearch(
  params: HttpParams | null | undefined,
  scopeParamName: string | null | undefined
): boolean {
  return !!scopeParamName && !!params?.get(scopeParamName);
}

function asCustomer(row: ParentFacilityDto): FacilityPickerRow {
  return {
    ...row,
    Source: 'Customer',
    SelectionKey: `Customer:${row.ParentFacilityID}`,
  };
}

function asProspect(row: ProspectFacilityDto): FacilityPickerRow {
  return {
    ...row,
    Source: 'Prospect',
    SelectionKey: `Prospect:${row.ProspectiveCustomerId}`,
  };
}

/**
 * US 36008: the facility search with prospect sites merged AFTER the Viewpoint rows. A customer-scoped
 * search never calls the prospect lookup. A failed prospect lookup is logged and contributes no rows,
 * so the Viewpoint rows still show; a failed Viewpoint lookup still errors, exactly as before.
 */
export function searchFacilitiesWithProspects(args: {
  customers$: Observable<ParentFacilityDto[]>;
  prospects: () => Observable<ProspectFacilityDto[]>;
  scoped: boolean;
}): Observable<FacilityPickerRow[]> {
  const customers$ = args.customers$.pipe(
    map((rows) => (rows ?? []).map(asCustomer))
  );
  if (args.scoped) {
    return customers$;
  }
  const prospects$ = args.prospects().pipe(
    map((rows) => (rows ?? []).map(asProspect)),
    catchError((err) => {
      console.error(
        '[dynamic-typeahead-facility] prospect lookup failed; showing Viewpoint facilities only',
        err
      );
      return of([] as FacilityPickerRow[]);
    })
  );
  return forkJoin([customers$, prospects$]).pipe(
    map(([customers, prospects]) => [...customers, ...prospects])
  );
}
